// src/components/ui/sections/BusinessNotificationsSection/BusinessNotificationsSectionShadcn.jsx
import React, { useState, useEffect } from 'react';
import { Bell, Mail, Plus, X, AlertCircle, CheckCircle } from 'lucide-react';

// shadcn components
import { Button } from '../../button';
import { Input } from '../../input';
import { Label } from '../../label';
import { Switch } from '../../switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../card';
import { Alert, AlertDescription } from '../../alert';
import { Badge } from '../../badge';

import { cn } from '../../../../utils/cn';
import { useSettings } from '../../../../hooks/useSettings';

const BusinessNotificationsSectionShadcn = ({ className = '' }) => {
  // Get notification data from settings context
  const {
    notificationPreferences,
    updateNotificationPreference,
    createDefaultNotificationPreferences,
    savingNotificationPrefs,
    getNotificationPreference,
    isNotificationEnabled,
    getNotificationRecipients,
    error
  } = useSettings();

  // Local state for email inputs
  const [emailInputs, setEmailInputs] = useState({
    appointment_scheduled: '',
    appointment_rescheduled: '',
    appointment_cancelled: ''
  });

  // Local state for managing which notification types have no defaults
  const [needsDefaults, setNeedsDefaults] = useState(false);

  // Check if we need to create default preferences
  useEffect(() => {
    if (notificationPreferences && notificationPreferences.length === 0) {
      setNeedsDefaults(true);
    } else {
      setNeedsDefaults(false);
    }
  }, [notificationPreferences]);

  // Event types configuration
  const eventTypes = [
    {
      key: 'appointment_scheduled',
      title: 'New Appointments',
      description: 'When customers book new appointments'
    },
    {
      key: 'appointment_rescheduled',
      title: 'Rescheduled Appointments',
      description: 'When customers reschedule existing appointments'
    },
    {
      key: 'appointment_cancelled',
      title: 'Cancelled Appointments',
      description: 'When customers cancel their appointments'
    }
  ];

  // Handle creating default preferences
  const handleCreateDefaults = () => {
    createDefaultNotificationPreferences();
  };

  // Handle toggling notification enabled/disabled
  const handleToggleNotification = (eventType, currentlyEnabled) => {
    const currentRecipients = getNotificationRecipients(eventType, 'email');
    updateNotificationPreference(eventType, 'email', !currentlyEnabled, currentRecipients);
  };

  // Handle adding email recipient
  const handleAddEmail = (eventType) => {
    const email = emailInputs[eventType].trim();

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      return;
    }

    const currentRecipients = getNotificationRecipients(eventType, 'email');

    // Check for duplicates
    if (currentRecipients.includes(email)) {
      return;
    }

    const newRecipients = [...currentRecipients, email];
    const isEnabled = isNotificationEnabled(eventType, 'email');

    updateNotificationPreference(eventType, 'email', isEnabled, newRecipients);

    // Clear input
    setEmailInputs(prev => ({ ...prev, [eventType]: '' }));
  };

  // Handle removing email recipient
  const handleRemoveEmail = (eventType, emailToRemove) => {
    const currentRecipients = getNotificationRecipients(eventType, 'email');
    const newRecipients = currentRecipients.filter(email => email !== emailToRemove);
    const isEnabled = isNotificationEnabled(eventType, 'email');

    updateNotificationPreference(eventType, 'email', isEnabled, newRecipients);
  };

  // Handle email input changes
  const handleEmailInputChange = (eventType, value) => {
    setEmailInputs(prev => ({ ...prev, [eventType]: value }));
  };

  // Handle Enter key in email input
  const handleEmailInputKeyPress = (eventType, event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddEmail(eventType);
    }
  };

  // If no preferences exist, show setup prompt
  if (needsDefaults) {
    return (
      <div className={cn("space-y-6 p-6", className)}>
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
            <Bell className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Set Up Business Notifications
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Get email notifications when customers book, reschedule, or cancel appointments.
          </p>
          <Button
            onClick={handleCreateDefaults}
            disabled={savingNotificationPrefs}
            size="lg"
            className="shadow-lg"
          >
            {savingNotificationPrefs ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Setting up...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Set Up Notifications
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-6 p-6", className)}>
      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Notification Types */}
      {eventTypes.map((eventConfig) => {
        const isEnabled = isNotificationEnabled(eventConfig.key, 'email');
        const recipients = getNotificationRecipients(eventConfig.key, 'email');
        const hasRecipients = recipients && recipients.length > 0;
        const isConfigured = isEnabled && hasRecipients;

        return (
          <Card key={eventConfig.key}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <CardTitle className="text-lg">
                      {eventConfig.title}
                    </CardTitle>
                    {isConfigured && (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    )}
                  </div>
                  <CardDescription>
                    {eventConfig.description}
                  </CardDescription>
                </div>

                {/* Toggle Switch */}
                <Switch
                  id={`toggle-${eventConfig.key}`}
                  checked={isEnabled}
                  onCheckedChange={(checked) => handleToggleNotification(eventConfig.key, isEnabled)}
                  disabled={savingNotificationPrefs}
                />
              </div>
            </CardHeader>

            {/* Email Recipients Section */}
            {isEnabled && (
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">
                    Email Recipients
                  </Label>
                </div>

                {/* Current Recipients */}
                {hasRecipients && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {recipients.map((email, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-2">
                        <span>{email}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveEmail(eventConfig.key, email)}
                          disabled={savingNotificationPrefs}
                          className="h-auto p-0 hover:bg-transparent hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Add Email Input */}
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={emailInputs[eventConfig.key]}
                    onChange={(e) => handleEmailInputChange(eventConfig.key, e.target.value)}
                    onKeyPress={(e) => handleEmailInputKeyPress(eventConfig.key, e)}
                    disabled={savingNotificationPrefs}
                    className="flex-1"
                  />
                  <Button
                    onClick={() => handleAddEmail(eventConfig.key)}
                    disabled={savingNotificationPrefs || !emailInputs[eventConfig.key].trim()}
                    size="sm"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Warning for no recipients */}
                {!hasRecipients && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Add at least one email address to receive notifications
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Summary Info */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Notification Status</Label>
          </div>
          <p className="text-muted-foreground text-sm">
            {notificationPreferences && notificationPreferences.filter(pref => pref.enabled).length} of {eventTypes.length} notification types enabled
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessNotificationsSectionShadcn;